import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IconComponent } from 'continity-shared-ui';

/**
 * Locked, frame-independent label contract for {@link DevLoginComponent} (ADO #1608).
 * Every user-facing string comes from here so the consuming app supplies i18n — the
 * component itself is purely presentational and ships no hardcoded copy.
 */
export interface DevLoginLabels {
  title: string;
  subtitle: string;
  emailLabel: string;
  emailPlaceholder: string;
  secretLabel: string;
  secretPlaceholder: string;
  submit: string;
  signingIn: string;
  emailInvalid: string;
  envBadge?: string;
}

/**
 * Presentational DEV credential-login card (`ca-dev-login`, ADO #1608). Frame-independent
 * and dumb: it validates the email shape and emits {@link devLogin} with `{ email, secret }`;
 * the host wires that to {@link DevAuthProvider.loginWithCredentials} and feeds
 * {@link isLoading} / {@link errorMessage} back in. No HTTP, no auth state, no i18n here —
 * all copy arrives via the required {@link labels} input (locked contract a later wave consumes).
 */
@Component({
  selector: 'ca-dev-login',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent],
  template: `
    <form (ngSubmit)="handleSubmit()" novalidate>
      <div
        class="relative overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 shadow-lg shadow-gray-900/5 dark:border-gray-700/80 dark:from-gray-800 dark:to-gray-800/60 dark:shadow-black/20"
        data-cy="dev-login-form"
      >
        <!-- Accent strip -->
        <div
          class="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500"
        ></div>

        <div class="p-6">
          <!-- Header -->
          <div class="mb-5 flex items-center gap-3">
            <span
              class="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-500/10 text-teal-600 ring-1 ring-inset ring-teal-500/20 dark:bg-teal-500/15 dark:text-teal-400 dark:ring-teal-500/30"
            >
              <app-icon name="code" [size]="20"></app-icon>
            </span>
            <div class="flex-1">
              <h2
                class="text-base font-semibold leading-tight text-gray-900 dark:text-white"
              >
                {{ labels.title }}
              </h2>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ labels.subtitle }}
              </p>
            </div>
            <span
              *ngIf="labels.envBadge"
              class="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-600 ring-1 ring-inset ring-amber-500/30 dark:bg-amber-500/15 dark:text-amber-400"
            >
              {{ labels.envBadge }}
            </span>
          </div>

          <!-- Error banner -->
          <div
            *ngIf="errorMessage"
            class="mb-4 rounded-lg border border-red-300 bg-red-50 text-red-800 dark:border-red-700/60 dark:bg-red-900/40 dark:text-red-200"
            data-cy="login-error-message"
          >
            <div class="flex items-start gap-2.5 px-3 py-2.5">
              <app-icon
                name="alert-circle"
                [size]="16"
                class="mt-0.5 flex-shrink-0 text-red-500 dark:text-red-400"
              ></app-icon>
              <p class="flex-1 text-sm font-medium">{{ errorMessage }}</p>
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <!-- Email -->
            <div class="form-group">
              <label
                for="caDevEmail"
                class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                {{ labels.emailLabel }}
              </label>
              <input
                type="email"
                id="caDevEmail"
                name="email"
                [(ngModel)]="email"
                (blur)="validateEmail()"
                class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-900/60 dark:text-white dark:placeholder-gray-500"
                [placeholder]="labels.emailPlaceholder"
                data-cy="dev-email-input"
                autocomplete="off"
              />
              <p
                *ngIf="emailError"
                class="mt-1 text-xs text-red-500 dark:text-red-400"
                data-cy="email-error"
              >
                {{ emailError }}
              </p>
            </div>

            <!-- Secret -->
            <div class="form-group">
              <label
                for="caDevSecret"
                class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                {{ labels.secretLabel }}
              </label>
              <div class="relative">
                <input
                  [type]="showSecret ? 'text' : 'password'"
                  id="caDevSecret"
                  name="secret"
                  [(ngModel)]="secret"
                  class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-900/60 dark:text-white dark:placeholder-gray-500"
                  [placeholder]="labels.secretPlaceholder"
                  data-cy="dev-secret-input"
                  autocomplete="off"
                />
                <button
                  type="button"
                  (click)="toggleSecret()"
                  tabindex="-1"
                  class="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  [attr.aria-label]="showSecret ? 'Hide secret' : 'Show secret'"
                >
                  <!-- shared-ui has an 'eye' icon but no 'eye-off'; show the eye to reveal,
                       and a small inline eye-off SVG (struck-through) to hide. -->
                  <app-icon
                    *ngIf="!showSecret"
                    name="eye"
                    [size]="16"
                  ></app-icon>
                  <svg
                    *ngIf="showSecret"
                    class="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L9.88 9.88"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Submit -->
            <button
              type="submit"
              [disabled]="isLoading || !email"
              class="mt-1 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
              data-cy="dev-login-submit"
            >
              <svg
                *ngIf="isLoading"
                class="-ml-1 h-4 w-4 animate-spin text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>{{ isLoading ? labels.signingIn : labels.submit }}</span>
            </button>
          </div>
        </div>
      </div>
    </form>
  `,
})
export class DevLoginComponent implements OnInit {
  /** Locked i18n label contract — all user-facing copy comes from here. */
  @Input({ required: true }) labels!: DevLoginLabels;
  /** Spinner + disabled submit while the host runs the login. */
  @Input() isLoading = false;
  /** Error banner text (cleared by the host on retry). */
  @Input() errorMessage = '';
  /** Pre-fills the secret field (e.g. the localhost mock secret). */
  @Input() defaultSecret = '';

  /** Emits the validated credentials for the host to POST to the dev-login endpoint. */
  @Output() devLogin = new EventEmitter<{ email: string; secret: string }>();

  email = '';
  secret = '';
  showSecret = false;
  emailError = '';

  private static readonly EMAIL_RE =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  ngOnInit(): void {
    if (this.defaultSecret) {
      this.secret = this.defaultSecret;
    }
  }

  /** Validates the current email; sets {@link emailError} on failure. Returns validity. */
  validateEmail(): boolean {
    if (!this.email || !DevLoginComponent.EMAIL_RE.test(this.email)) {
      this.emailError = this.labels.emailInvalid;
      return false;
    }
    this.emailError = '';
    return true;
  }

  /** Validate, then emit `{ email, secret }` for the host to process. */
  handleSubmit(): void {
    if (!this.email || !this.validateEmail()) {
      return;
    }
    this.devLogin.emit({ email: this.email, secret: this.secret });
  }

  /** Toggle the secret field between password and plain text. */
  toggleSecret(): void {
    this.showSecret = !this.showSecret;
  }
}
